
import React, { useState, useEffect, useCallback } from "react";
import { DynamicAppSidebar } from "./DynamicAppSidebar";
import { SettingsSidebar } from "./SettingsSidebar";
import BrandSelector from "@/components/BrandSelector";
import UserMenu from "@/components/UserMenu";
import { GlobalSearch } from "@/components/navigation/GlobalSearch";
import { MobileHeader } from "@/components/mobile/MobileHeader";
import { FloatingActionBar } from "@/components/mobile/FloatingActionBar";
import { useIsMobile } from "@/hooks/use-mobile";
import { TaskSearchModal } from "@/components/search/TaskSearchModal";
import { SidebarTrigger, SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useBrandAddons } from "@/hooks/useBrandAddons";
import { useQuickAdd } from "@/contexts/QuickAddContext";
import { BrandSelectionDialog } from "@/components/brand-selector/BrandSelectionDialog";
import { CreateBrandDialog } from "@/components/brand-selector/CreateBrandDialog";
import { NotesWidget } from "@/components/notes/NotesWidget";
import { NotificationDropdown } from "@/components/notifications/NotificationDropdown";
import { HeaderNotifications } from "./HeaderNotifications";
import { Button } from "@/components/ui/button";
import { Plus, PieChart, Activity, MessageCircle, Search, Mic, Calendar, Send, Sliders, LayoutGrid } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { SupportChatInterface } from "@/components/support/SupportChatInterface";
import { useSupportUnreadCount } from "@/hooks/useSupportUnreadCount";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AiAssistantToggle } from "@/components/ai-assistant/AiAssistantToggle";
import { useNavigate, useLocation } from "react-router-dom";
import { Footer } from "./Footer";
import { TrialWarningBar } from "@/components/TrialWarningBar";
import { VoiceOverlay } from "@/components/voice/VoiceOverlay";
import { useVoiceOverlay } from "@/hooks/useVoiceOverlay";
import { ControlsOverlay } from "@/components/overlays/ControlsOverlay";
import { WidgetsOverlay } from "@/components/overlays/WidgetsOverlay";
import { useControlsOverlay } from "@/hooks/useControlsOverlay";
import { useWidgetsOverlay } from "@/hooks/useWidgetsOverlay";
import { useProfileOverlayPreferences } from "@/hooks/useProfileOverlayPreferences";
import { MobileModePrompt } from "@/components/mobile/MobileModePrompt";
import { useSettings, SettingsSidebarInAppSheetContext } from "@/contexts/SettingsContext";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import { MobileFooterNav } from "@/components/mobile/MobileFooterNav";
import { useSidebar } from "@/components/ui/sidebar";

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout = ({ children }: AppLayoutProps) => {
  const [isChangingBrand, setIsChangingBrand] = useState(false);
  const [showBrandSelection, setShowBrandSelection] = useState(false);
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [activityDropdownOpen, setActivityDropdownOpen] = useState(false);
  const [isTaskSearchOpen, setIsTaskSearchOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>();
  const { user } = useAuth();
  const { brands, currentBrandId, selectBrand, createBrand, isInitializing } = useBrandManager();
  const { hasAddonAccess, isLoading: isLoadingAddons } = useBrandAddons();
  const { setIsQuickAddOpen } = useQuickAdd();
  const navigate = useNavigate();
  const location = useLocation();
  const unreadCount = useSupportUnreadCount();

  // Holdco view routes have their own brand context - don't require global brand selection
  const isHoldcoViewRoute = location.pathname.startsWith('/holdco-views/') &&
    !location.pathname.startsWith('/holdco-views/settings') &&
    location.pathname !== '/holdco-views' &&
    location.pathname !== '/holdco-views/create';
  const { isOpen: isVoiceOverlayOpen, openOverlay: openVoiceOverlay, closeOverlay: closeVoiceOverlay } = useVoiceOverlay();
  const { isOpen: isControlsOverlayOpen, openOverlay: openControlsOverlay, closeOverlay: closeControlsOverlay } = useControlsOverlay();
  const { isOpen: isWidgetsOverlayOpen, openOverlay: openWidgetsOverlay, closeOverlay: closeWidgetsOverlay } = useWidgetsOverlay();
  const { controlsDashboardId, widgetsDashboardId } = useProfileOverlayPreferences(currentBrandId ?? undefined);
  const isMobile = useIsMobile();
  const [showMobileModePrompt, setShowMobileModePrompt] = useState(false);
  const { isSettingsSidebarOpen, setIsSettingsSidebarOpen } = useSettings();
  const { toggleSidebar, setOpenMobile } = useSidebar();

  const openAiChat = () => {
    if (!hasAddonAccess('ai_assistant')) {
      toast.error('AI Assistant access is not provisioned for this brand');
      return;
    }
    setIsAiChatOpen(!isAiChatOpen);
  };

  const handleVoiceAssistant = () => {
    if (!hasAddonAccess('voice_assistant')) {
      toast.error('Voice Assistant access is not provisioned for this brand');
      return;
    }
    openVoiceOverlay();
  };

  // Show brand selection dialog when user has brands but no current selection
  // Skip on holdco view routes - they have their own brand context from the view
  useEffect(() => {
    if (isHoldcoViewRoute) {
      setShowBrandSelection(false);
      return;
    }
    if (user && !isInitializing && brands.length > 0 && !currentBrandId) {
      setShowBrandSelection(true);
    } else {
      setShowBrandSelection(false);
    }
  }, [user, brands, currentBrandId, isInitializing, isHoldcoViewRoute]);

  const handleBrandSelect = useCallback((brandId: string) => {
    if (brandId === currentBrandId) {
      return;
    }
    
    setIsChangingBrand(true);
    
    // Always call selectBrand from useBrandManager first
    selectBrand(brandId);
    
    // Reset the changing state after a short delay
    setTimeout(() => {
      setIsChangingBrand(false);
    }, 300);
  }, [currentBrandId, selectBrand]);

  const handleBrandSelectionFromDialog = (brandId: string) => {
    setShowBrandSelection(false);
    handleBrandSelect(brandId);
  };

  const handleCreateBrandFromDialog = () => {
    setShowBrandSelection(false);
    setShowCreateBrand(true);
  };

  const handleCreateBrand = async (name: string) => {
    await createBrand(name);
    setShowCreateBrand(false);
  };

  useEffect(() => {
    const handleBrandChanged = (event: CustomEvent) => {
      if (event.detail && event.detail.brandId) {
        setIsChangingBrand(true);
        setTimeout(() => {
          setIsChangingBrand(false);
        }, 300);
      }
    };

    window.addEventListener('brandChanged', handleBrandChanged as EventListener);
    
    return () => {
      window.removeEventListener('brandChanged', handleBrandChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (isMobile && !sessionStorage.getItem('mobileModePromptShown')) {
      setShowMobileModePrompt(true);
      sessionStorage.setItem('mobileModePromptShown', '1');
    }
  }, [isMobile]);

  return (
    <div className="flex w-full min-h-screen min-w-0 relative">
      {/* Left slot: on mobile always main sidebar (settings open as Sheet overlay); on desktop main or settings */}
      {isMobile ? (
        <DynamicAppSidebar />
      ) : isSettingsSidebarOpen ? (
        <>
          <div className="shrink-0 w-64 min-w-64 h-svh bg-transparent" aria-hidden />
          <div className="fixed inset-y-0 left-0 z-10 flex h-svh w-64 flex-col overflow-hidden border-r border-t-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 [&_[data-sidebar=header]]:border-t-0">
            <SettingsSidebar />
          </div>
          <div
            className="fixed inset-y-0 left-64 right-0 z-[9] cursor-default"
            onClick={() => setIsSettingsSidebarOpen(false)}
            onKeyDown={(e) => e.key === "Escape" && setIsSettingsSidebarOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close settings"
          />
        </>
      ) : (
        <DynamicAppSidebar />
      )}
      {/* Mobile: settings open as Sheet overlay (like main sidebar) so closing leaves no white space */}
      {isMobile && (
        <Sheet
          open={isSettingsSidebarOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsSettingsSidebarOpen(false);
              setOpenMobile(false);
            }
          }}
        >
          <SheetContent side="left" className="w-[18rem] max-w-[85vw] !p-0 !pt-0 bg-background/95 backdrop-blur border-0 [&>button]:hidden">
            <SettingsSidebarInAppSheetContext.Provider value={true}>
              <SettingsSidebar />
            </SettingsSidebarInAppSheetContext.Provider>
          </SheetContent>
        </Sheet>
      )}
      <SidebarInset className="flex-1 min-w-0 overflow-x-auto">
        <TrialWarningBar />
        {isMobile ? (
          <>
            <MobileHeader />
            <FloatingActionBar
              onQuickAdd={() => setIsQuickAddOpen(true)}
              onTaskSearch={() => setIsTaskSearchOpen(true)}
              onVoiceAssistant={handleVoiceAssistant}
              onAiAssistant={openAiChat}
              onNotifications={() => navigate('/reporting-dashboard')}
              onReporting={() => navigate('/reporting-dashboard')}
              onActivity={() => navigate('/my-overall-activity')}
            />
          </>
        ) : (
          <header className="sticky top-0 z-40 bg-background h-16 border-b px-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="mr-2" />
              <GlobalSearch />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8"
                      onClick={() => setIsQuickAddOpen(true)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Create new task</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8"
                      onClick={() => setIsTaskSearchOpen(true)}
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Search all tasks</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8 relative"
                      onClick={() => setIsSupportOpen(true)}
                    >
                      <Send className="h-4 w-4" />
                      {unreadCount > 0 && (
                        <Badge
                          variant="destructive"
                          className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                        >
                          {unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Support Chat</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8"
                      onClick={openControlsOverlay}
                    >
                      <Sliders className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Controls</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8"
                      onClick={openWidgetsOverlay}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Widgets</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HeaderNotifications />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Job Notifications</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => navigate('/reporting-dashboard')}
                      >
                        <PieChart className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reporting Dashboard</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
               <DropdownMenu open={activityDropdownOpen} onOpenChange={setActivityDropdownOpen}>
                 <DropdownMenuTrigger asChild>
                   <div
                     onMouseEnter={() => setActivityDropdownOpen(true)}
                     onMouseLeave={() => setActivityDropdownOpen(false)}
                   >
                     <Button
                       variant="ghost"
                       size="icon"
                       className="h-8 w-8"
                     >
                       <Activity className="h-4 w-4" />
                     </Button>
                   </div>
                 </DropdownMenuTrigger>
                 <DropdownMenuContent 
                   align="end" 
                   className="bg-muted border-0 shadow-lg z-50 w-36 p-0"
                   onMouseEnter={() => setActivityDropdownOpen(true)}
                   onMouseLeave={() => setActivityDropdownOpen(false)}
                 >
                   <DropdownMenuLabel className="text-muted-foreground font-medium bg-muted px-3 py-2 m-0">
                     Activity
                   </DropdownMenuLabel>
                   <DropdownMenuSeparator className="bg-border/50 m-0" />
                   <DropdownMenuItem 
                     onClick={() => navigate('/my-overall-dashboard')}
                     className="cursor-pointer focus:bg-muted hover:bg-muted bg-background border-0 rounded-none"
                   >
                     Today's View
                   </DropdownMenuItem>
                   <DropdownMenuItem 
                     onClick={() => navigate('/my-overall-activity')}
                     className="cursor-pointer focus:bg-muted hover:bg-muted bg-background border-0 rounded-none"
                   >
                     Today's Activity
                   </DropdownMenuItem>
                 </DropdownMenuContent>
               </DropdownMenu>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-8 w-8",
                          !hasAddonAccess('voice_assistant') && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={handleVoiceAssistant}
                        disabled={!hasAddonAccess('voice_assistant')}
                      >
                        <Mic className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{hasAddonAccess('voice_assistant') ? 'Voice Assistant' : 'No access'}</p>
                    </TooltipContent
                    >
                   </Tooltip>
                 </TooltipProvider>
                 <TooltipProvider>
                   <Tooltip>
                     <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-8 w-8",
                          !hasAddonAccess('ai_assistant') && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={openAiChat}
                        disabled={!hasAddonAccess('ai_assistant')}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{isLoadingAddons ? 'Loading...' : hasAddonAccess('ai_assistant') ? 'AI Assistant' : 'No access'}</p>
                    </TooltipContent>
                   </Tooltip>
                 </TooltipProvider>
               <NotesWidget />
               <NotificationDropdown 
                 key={currentBrandId || 'no-brand'}
                 onTaskClick={(taskId, taskTable) => {
                   // For now, we'll just navigate to the task detail page
                   navigate(`/tasks/${currentBrandId}/${taskId}`);
                 }} 
               />
             </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => navigate('/content-calendar')}
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Content Calendar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <BrandSelector 
              onBrandSelect={handleBrandSelect} 
              currentBrandId={currentBrandId} 
            />
            <UserMenu />
          </div>
        </header>
        )}

        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <main className={cn(
            "relative flex-1 p-4 md:p-6 bg-background overflow-y-auto overscroll-y-auto min-h-0",
            isMobile && "min-h-[calc(100vh-64px)]",
            isMobile && "min-h-[100dvh]"
          )}>
            {/* Gradient overlay - top 20% of viewport */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[20vh] bg-gradient-to-b from-slate-200 to-transparent dark:from-slate-950 dark:to-transparent"
              aria-hidden
            />
            <div className="relative z-10 max-w-7xl mx-auto">
              {isChangingBrand ? (
                <div className="animate-pulse space-y-6">
                  <div className="h-8 w-64 bg-gray-200 rounded"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              ) : (
                <div className="animate-in fade-in duration-300">
                  {children}
                 </div>
               )}
            </div>
            {isMobile && <div className="h-20" />}
          </main>
          <Footer />
         </div>
        
        {/* Mobile Footer Navigation */}
        <MobileFooterNav
          onQuickAdd={() => setIsQuickAddOpen(true)}
          onToggleSidebar={toggleSidebar}
        />
      </SidebarInset>

      <BrandSelectionDialog
        open={showBrandSelection}
        brands={brands}
        onBrandSelect={handleBrandSelectionFromDialog}
        onCreateBrand={handleCreateBrandFromDialog}
      />

      <CreateBrandDialog 
        open={showCreateBrand} 
        onOpenChange={setShowCreateBrand} 
        onCreateBrand={handleCreateBrand} 
      />
      
      <AiAssistantToggle 
        isOpen={isAiChatOpen}
        onToggle={() => setIsAiChatOpen(!isAiChatOpen)}
      />
      
      <TaskSearchModal
        isOpen={isTaskSearchOpen}
        onClose={() => setIsTaskSearchOpen(false)}
      />
      
      <VoiceOverlay
        isOpen={isVoiceOverlayOpen}
        onClose={closeVoiceOverlay}
      />

      <ControlsOverlay
        isOpen={isControlsOverlayOpen}
        onClose={closeControlsOverlay}
        controlsDashboardId={controlsDashboardId}
      />

      <WidgetsOverlay
        isOpen={isWidgetsOverlayOpen}
        onClose={closeWidgetsOverlay}
        widgetsDashboardId={widgetsDashboardId}
      />

      <MobileModePrompt
        isOpen={showMobileModePrompt}
        onOpenChange={setShowMobileModePrompt}
        onDashboard={() => navigate('/dashboard')}
        onVoice={() => { setShowMobileModePrompt(false); handleVoiceAssistant(); }}
        onChat={() => { setShowMobileModePrompt(false); openAiChat(); }}
      />

      {/* Support Dialog */}
      <Dialog open={isSupportOpen} onOpenChange={setIsSupportOpen}>
        <DialogContent className="max-w-2xl h-[500px] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogTitleWithBug
            popupName="Support Chat"
            popupContext={window.location.pathname}
            isOpen={isSupportOpen}
            className="sr-only"
          >
            Support Chat
          </DialogTitleWithBug>
          <DialogDescription className="sr-only">Chat with support</DialogDescription>
          <div className="flex-1 min-h-0">
            <SupportChatInterface
              selectedThreadId={selectedThreadId}
              onSelectThread={(id) => { console.log('support: setSelectedThreadId', id); setSelectedThreadId(id); }}
              onClose={() => setIsSupportOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
